import { DataTable } from "primereact/datatable";
import MeaDataTableLoadingQuery from "./MeaDataTableLoadingQuery";
import { Toolbar } from "primereact/toolbar";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import useUrlState from "@ahooksjs/use-url-state";
import { InputSwitch } from "primereact/inputswitch";
import MeaButton from "./MeaButton"
import { Toast } from "primereact/toast";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Checkbox } from "primereact/checkbox";
import { useAuthContext } from "../hooks/useAuthContext";

// Styles for pagination positioning
const paginationStyles = `
  .pagination-top .p-paginator {
    margin-bottom: 1rem;
  }
  .pagination-top .p-paginator-bottom {
    margin-top: 1rem;
    margin-bottom: 0;
  }
  .pagination-top .p-paginator-top {
    margin-bottom: 1rem;
  }
  .pagination-top .p-paginator-bottom {
    margin-top: 1rem;
    margin-bottom: 0;
  }
`;

const MeaDataTable = forwardRef((props, ref) => {
  let {
    children,
    query,
    onChangeCallback,
    serviceAPI,
    names,
    showToolbar = true,
    startToolbarTemplate,
    selectedToolbarTemplate,
    endToolbarTemplate,
    showDeleteButton = true,
    showFilterButton = true,
    showFilter = false,
    showTotalSelectedElements = false,
    style = {},
    setPage,
    setPageSize,
    rowClassName,
    paginator = true,
    showHeader = true,
    schoolId,
    trainingId,
    accessToken,
    filters = undefined,
    onFilterCallback = null,
    // Add prop for custom filter state
    customFilterState = null,
    // Add prop to exclude specific filter fields from auto-show behavior
    // Useful for fields like "status" that have default values but shouldn't trigger filter display
    excludeFilterFields = [],
    // Add props for initial page and page size
    page: initialPage = 1,
    pageSize: initialPageSize = 50,
    showSelection = true,
    // Add prop for pagination position (top, bottom, or both)
    paginatorPosition = "top",
    scrollable = false,
    scrollHeight = "auto",
    loading,
  } = props;
  //
  useEffect(() => {
    // cleanup selected elements if refetching
    setSelectedElements([])
  }, [query?.isRefetching])
  //
  const [showFilterContainer, setShowFilterContainer] = useState(showFilter);
  const [activeFilters, setActiveFilters] = useState({});
  const [filterCount, setFilterCount] = useState(0);
  //
  const [confirmDialogVisible, setConfirmDialogVisible] = useState(false);
  const toast = useRef(null);
  const jsonString =
    '{"' +
    names?.pagination +
    'Page": "' + initialPage + '", "' +
    names?.pagination +
    'PageSize": "' + initialPageSize + '"}';

  const [state, setState] = useUrlState(JSON.parse(jsonString));
  useEffect(() => {
    setPage && setPage(state[names?.pagination + "Page"]);
    setPageSize && setPageSize(state[names?.pagination + "PageSize"]);
  }, [state]);

  // Set initial page and page size when component mounts
  useEffect(() => {
    setPage && setPage(initialPage);
    setPageSize && setPageSize(initialPageSize);
  }, []);

  const [selectedElements, setSelectedElements] = useState(null);
  const tableLoading = loading !== undefined ? loading : query?.isLoading || query?.isFetching;
  //
  useImperativeHandle(ref, () => ({
    getSelectedElements: () => {
      return selectedElements;
    },
    // Add filter-related methods
    getActiveFilters: () => activeFilters,
    getFilterCount: () => filterCount,
    hasActiveFilters: () => filterCount > 0,
    clearAllFilters: () => {
      setActiveFilters({});
      setFilterCount(0);
      // You might need to trigger a refetch or reset the DataTable
    }
  }));
  
  // Handle column filter events (for PrimeReact native filters)
  const handleColumnFilter = (event) => {
    //console.log("handleColumnFilter", event);
    const { filters } = event;

    onFilterCallback && onFilterCallback(event);
    
    // Update active filters state
    setActiveFilters(filters);
    
    // Check if any filters are active
    let count = 0;
    Object.keys(filters).forEach(key => {
      const filter = filters[key];
      if (Array.isArray(filter)) {
        filter.forEach(f => {
          if (f.value && f.value !== '') count++;
        });
      } else if (filter && filter.value && filter.value !== '') {
        count++;
      }
    });
    
    setFilterCount(count);
    
    // Auto-show filter container if filters are active
    if (count > 0 && !showFilterContainer) {
      setShowFilterContainer(true);
    }
    
    // Optionally auto-hide when no filters
    if (count === 0 && showFilterContainer && !showFilter) {
      setShowFilterContainer(false);
    }
  };

  
  // Handle custom filter state changes
  useEffect(() => {
    if (customFilterState) {
      let count = 0;
      //console.log("customFilterState", customFilterState);
      //console.log("excludeFilterFields", excludeFilterFields);
      // Handle different types of custom filter state
      if (typeof customFilterState === 'string') {
        try {
          const filterObj = JSON.parse(customFilterState);
          count = Object.entries(filterObj).filter(([key, value]) => 
            // Only count filters that have values AND are not in the exclusion list
            value && value !== "" && !excludeFilterFields.includes(key)
          ).length;
        } catch (e) {
          console.error('Error parsing custom filter state:', e);
        }
      } else if (typeof customFilterState === 'object') {
        count = Object.entries(customFilterState).filter(([key, value]) => 
          // Only count filters that have values AND are not in the exclusion list
          value && value !== "" && !excludeFilterFields.includes(key)
        ).length;
      }
      
      //console.log("Filter count (excluding excluded fields):", count);
      setFilterCount(count);
      
      // Auto-show filter container if custom filters are active
      if (count > 0 && !showFilterContainer) {
        setShowFilterContainer(true);
      }
      
    }
  }, [customFilterState, excludeFilterFields, showFilterContainer, showFilter]);
  
  // Persist filter state in localStorage
  useEffect(() => {
    if (filterCount > 0) {
      // Store in localStorage
      localStorage.setItem('dataTableFilters', JSON.stringify(activeFilters));
    } else {
      localStorage.removeItem('dataTableFilters');
    }
  }, [activeFilters, filterCount]);
  
  // Restore filters on mount
  useEffect(() => {
    const savedFilters = localStorage.getItem('dataTableFilters');
    if (savedFilters) {
      try {
        const filters = JSON.parse(savedFilters);
        setActiveFilters(filters);
        setFilterCount(Object.keys(filters).length);
        setShowFilterContainer(true);
      } catch (e) {
        console.error('Error restoring filters:', e);
      }
    }
  }, []);
  //
  const onPageChange = (e) => {
    let newPage = e.page + 1;
    let newPageSize = Number(e.rows);
    if (e.rows !== parseInt(state[names?.pagination + "PageSize"])) {
      newPage = 1;
    }
    const jsonString =
      '{"' +
      names?.pagination +
      'Page": "' +
      newPage +
      '", "' +
      names?.pagination +
      'PageSize": "' +
      newPageSize +
      '"}';
    setState((s) => JSON.parse(jsonString));
    setPage(newPage);
    setPageSize(newPageSize);
    setSelectedElements([]);
  };

  const _startToolbarTemplate = (
    <div className="flex justify-content-between align-items-center">
      {startToolbarTemplate}
      {selectedElements?.length > 0 && (
        selectedToolbarTemplate
      )}
      {selectedElements?.length > 0 && showDeleteButton && (
        <MeaButton
          label="Supprimer"
          size="small"
          icon="pi pi-trash"
          severity="danger"
          className="p-button mr-2 action"
          onClick={() => {
            setConfirmDialogVisible(true);
          }}
          data-pr-tooltip="Create new car"
        />

      )}
    </div>
  );

  const _endToolbarTemplate = (
    <div className="flex justify-content-between align-items-center">
      {endToolbarTemplate}
      {showFilterButton && (
        <>
          <label htmlFor="chkFilterDisplay" className="font-semibold mr-2 ml-2">
            Afficher les filtres
          </label>
          <InputSwitch
            inputId="chkFilterDisplay"
            className="mr-2"
            checked={showFilterContainer}
            disabled={filterCount > 0}
            aria-label="Switch filter display between menu and row"
            tooltipOptions={{ position: "top" }}
            onChange={(e) => {
              setShowFilterContainer(!showFilterContainer);
            }}
            tooltip={filterCount > 0 ? "Désactivé - filtres actifs" : "Afficher les filtres"}
          />
        </>
      )}

    </div>
  );

  const shouldRenderStandaloneToolbar =
    showToolbar && paginatorPosition === "bottom";
  const paginatorToolbarLeft = shouldRenderStandaloneToolbar
    ? null
    : showToolbar && _startToolbarTemplate;
  const paginatorToolbarRight = shouldRenderStandaloneToolbar
    ? null
    : showToolbar && _endToolbarTemplate;

  const handleUpdateElement = async (e) => {
    let { newData, index } = e;

    try {
      await serviceAPI.UpdateOne(
        schoolId,
        trainingId,
        accessToken,
        newData?.id,
        newData
      );
      query.refetch();
      toast.current.clear();
      toast.current.show({
        severity: "success",
        summary: "Succès",
        detail: "Eléments mis à jour!",
        life: 3000,
      });
    } catch (err) {
      toast.current.clear();
      toast.current.show({
        severity: "error",
        summary: "Erreur",
        detail: "Erreur de mise à jour de l'élément sélectionné!",
        life: 3000,
      });
    }
  };

  const handleDeleteSelected = async (e) => {
    toast.current.show({
      severity: "info",
      summary: "Info",
      sticky: true,
      detail: "Supression des éléments sélectionnés...",
      life: 3000,
    });

    try {
      await Promise.all(
        selectedElements.map((element) => {
          return serviceAPI.DeleteOne(schoolId, trainingId, accessToken, element?.id);
        })
      );
      toast.current.clear();
      toast.current.show({
        severity: "success",
        summary: "Succès",
        detail: "Eléments supprimés!",
        life: 3000,
      });
      setSelectedElements([]);
      onChangeCallback?.();
      query?.refetch();
    } catch (err) {
      toast.current.clear();
      toast.current.show({
        severity: "error",
        summary: "Erreur",
        detail: "Erreur de supression des éléments sélectionnés!",
        life: 3000,
      });
    }
  };

  return (
    <>
      <style>{paginationStyles}</style>

      {shouldRenderStandaloneToolbar && (
        <Toolbar
          className="mb-3"
          start={_startToolbarTemplate}
          end={_endToolbarTemplate}
        />
      )}

      <DataTable
        header={showHeader ? undefined : null}
        style={style}
        className={`bg-white ${paginatorPosition === "top" || paginatorPosition === "both" ? "pagination-top" : ""}`}
        rowClassName={rowClassName}
        loading={tableLoading}
        emptyMessage={<MeaDataTableLoadingQuery query={query} />}
        value={query?.data?.data || []}
        lazy
        dataKey="id"
        scrollable={scrollable}
        scrollHeight={scrollHeight}
        // pagination
        paginator={paginator}
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        currentPageReportTemplate={
          "{first} de {last} sur {totalRecords} " + names?.plural
        }
        paginatorLeft={paginatorToolbarLeft} paginatorRight={paginatorToolbarRight}
        rowsPerPageOptions={[10, 30, 50, 100, 500]}
        totalRecords={query?.data?.meta?.total || 0}
        onPage={onPageChange}
        first={
          (parseInt(state[names?.pagination + "Page"]) - 1) *
          parseInt(state[names?.pagination + "PageSize"])
        }
        rows={parseInt(state[names?.pagination + "PageSize"])}
        // paginatorPosition: "top" | "bottom" | "both" - controls pagination placement
        paginatorPosition={paginatorPosition}
        // selection
        selectionMode={showSelection ? true : undefined}
        selection={showSelection ? selectedElements : null}
        onSelectionChange={(e) => {
          if (showSelection) {
            setSelectedElements(e.value)
          }
        }}
        size="small"
        filters={filters}
        filterDisplay={showFilterContainer ? "row" : ""}
        //globalFilterFields={["weekday"]}
        // edit
        editMode="row"
        onRowEditComplete={handleUpdateElement}
        // filter events
        onFilter={handleColumnFilter}
      >
        {children}
      </DataTable>
      {showTotalSelectedElements && selectedElements?.length > 0 && (
        <div className="flex align-items-center mt-3">
          <Checkbox
            inputId="ingredient1"
            name="pizza"
            value="Cheese"
            onChange={() => {
              setSelectedElements([]);
            }}
            checked={selectedElements?.length > 0}
          />
          <label htmlFor="ingredient1" className="ml-2">
            <span>Eléments sélectionnés : </span>
            <b>{selectedElements?.length}</b>
          </label>
        </div>
      )}
      <Toast ref={toast} position="bottom-center" />
      <ConfirmDialog
        group="declarative"
        visible={confirmDialogVisible}
        onHide={() => setConfirmDialogVisible(false)}
        message="Voulez-vous vraiment supprimer les éléments séléctionnés?"
        header="Confirmation"
        icon="pi pi-exclamation-triangle"
        acceptLabel="Oui"
        rejectLabel="Non"
        accept={handleDeleteSelected}
        reject={() => { }}
      />
    </>
  );
});

export default MeaDataTable;
